import {
	useGetAdviceMaintenancePreviewDemand,
	useGetDynamicCyclePhases,
	useGetParcellingById,
} from '@practium/features/api';
import type { ParcellingMaturationCycle } from '@practium/features/parcelling';
import { useEffect, useMemo, useState } from 'react';
import { buildMaintenanceStartPayload } from '../services/maintenanceStartPayload';
import type {
	FertilizationPlanAdvice,
	MaintenanceFormData,
	MaintenanceParcellingType,
	ParcellingBalanceUnit,
} from '../types';
import { buildCycleCalendarMonths, parseCycleDate } from '../utils/dynamicParcellingCycle';
import {
	getMaintenanceMaturationCycle,
	getMaintenanceParcellingType,
	getMaintenanceReferenceId,
	normalizeMaintenanceDate,
} from '../utils/maintenanceAdviceMapper';
import {
	FLOWERING_PHASES,
	getParcellingMonthsByPhase,
	type MaintenanceParcellingContext,
} from '../utils/parcellingCycle';
import {
	calculatePlantsByHa,
	mapDemandToTableRows,
	mapParcellingToTableRows,
} from '../utils/parcellingTableMapper';

const MONTH_PATTERN = /^\d{4}-\d{2}$/;

interface ParcellingSector {
	_id: string;
	size?: number;
	amountOfPlants?: number;
}

interface DynamicParcellingOption {
	_id: string;
	maturationCycle?: ParcellingMaturationCycle;
}

interface UseMaintenanceParcellingOptions {
	form: MaintenanceFormData;
	dynamicParcellings: DynamicParcellingOption[];
	set: <K extends keyof MaintenanceFormData>(key: K, value: MaintenanceFormData[K]) => void;
	viewData?: FertilizationPlanAdvice;
	sectors: ParcellingSector[];
	isView: boolean;
	isEdit: boolean;
	onContextChange: (context: MaintenanceParcellingContext) => void;
}

export const useMaintenanceParcelling = ({
	form,
	dynamicParcellings,
	set,
	viewData,
	sectors,
	isView,
	isEdit,
	onContextChange,
}: UseMaintenanceParcellingOptions) => {
	const usesSavedDemand = isView || isEdit;
	const [balanceUnit, setBalanceUnit] = useState<ParcellingBalanceUnit>('percentage');
	const selectedParcellingId = isView
		? getMaintenanceReferenceId(viewData?.parcelling)
		: form.parcelling;
	const { data: selectedParcelling } = useGetParcellingById(selectedParcellingId);
	const parcellingType =
		isView && viewData ? getMaintenanceParcellingType(viewData) : form.parcellingType;
	const isDynamic = parcellingType === 'dynamic';
	const floweringDate = isView
		? normalizeMaintenanceDate(viewData?.floweringDate)
		: form.floweringDate;
	const hasFloweringDate = parseCycleDate(floweringDate) != null;
	const {
		data: cycle,
		isFetching: isFetchingCycle,
		error: cycleError,
	} = useGetDynamicCyclePhases(selectedParcellingId, floweringDate, isDynamic && hasFloweringDate);
	const maturationCycle = form.maturationCycle || getMaintenanceMaturationCycle(selectedParcelling);

	const floweringMonths = useMemo(
		() => getParcellingMonthsByPhase(selectedParcelling?.fixedParcel, FLOWERING_PHASES),
		[selectedParcelling]
	);

	useEffect(() => {
		onContextChange({ floweringMonths });
	}, [floweringMonths, onContextChange]);

	useEffect(() => {
		if (form.parcellingType || !selectedParcelling?.typeParcelling) return;
		set('parcellingType', selectedParcelling.typeParcelling);
	}, [form.parcellingType, selectedParcelling?.typeParcelling, set]);

	const isPreviewReady =
		!usesSavedDemand &&
		balanceUnit !== 'percentage' &&
		!!form.prop &&
		!!form.area &&
		form.sectors.length > 0 &&
		!!form.soilAnalysis &&
		!!form.cropStage &&
		!!form.parcellingType &&
		!!form.parcelling &&
		(isDynamic ? hasFloweringDate : MONTH_PATTERN.test(form.floweringMonth));

	const previewPayload = useMemo(
		() => (isPreviewReady ? buildMaintenanceStartPayload({ ...form, name: '' }) : null),
		[isPreviewReady, form]
	);

	const {
		data: previewDemand,
		isFetching: isFetchingPreview,
		error: previewError,
	} = useGetAdviceMaintenancePreviewDemand(previewPayload);

	const demandSource = useMemo(
		() =>
			usesSavedDemand
				? {
						periods: viewData?.periods,
						demandByPeriod: viewData?.demandByPeriod,
						demandAnnual: viewData?.demandAnnual,
					}
				: previewDemand,
		[
			previewDemand,
			usesSavedDemand,
			viewData?.demandAnnual,
			viewData?.demandByPeriod,
			viewData?.periods,
		]
	);

	const selectedSectorIds = useMemo(
		() =>
			usesSavedDemand ? (viewData?.sectors ?? []).map(getMaintenanceReferenceId) : form.sectors,
		[form.sectors, usesSavedDemand, viewData?.sectors]
	);
	const plantsByHa = useMemo(
		() => calculatePlantsByHa(sectors.filter((sector) => selectedSectorIds.includes(sector._id))),
		[sectors, selectedSectorIds]
	);

	const previewRows = useMemo(() => {
		if (balanceUnit === 'percentage') {
			return mapParcellingToTableRows(selectedParcelling, cycle);
		}
		return mapDemandToTableRows(demandSource, selectedParcelling, balanceUnit, plantsByHa, cycle);
	}, [balanceUnit, cycle, demandSource, plantsByHa, selectedParcelling]);

	const cycleCalendar = useMemo(() => {
		if (!isDynamic || !cycle?.phases.length) return null;
		return { floweringDate, phases: cycle.phases, months: buildCycleCalendarMonths(cycle.phases) };
	}, [cycle, floweringDate, isDynamic]);

	const previewEmptyMessage =
		(isDynamic ? cycleError?.message : undefined) ??
		previewError?.message ??
		'Preencha as informações gerais para visualizar a demanda parcelada.';

	const showParcellingFields = isEdit || !!form.parcellingType;
	const showParcellingPreview =
		!!selectedParcelling &&
		(usesSavedDemand || !!form.parcelling) &&
		(!isDynamic || hasFloweringDate);

	const resetParcellingFields = () => {
		set('parcelling', '');
		set('maturationCycle', '');
	};

	const setParcellingType = (value: MaintenanceParcellingType) => {
		if (value === form.parcellingType) return;
		set('parcellingType', value);
		set('floweringMonth', '');
		set('floweringDate', '');
		resetParcellingFields();
	};

	const setMaturationCycle = (value: ParcellingMaturationCycle) => {
		set('maturationCycle', value);
		set(
			'parcelling',
			dynamicParcellings.find((parcelling) => parcelling.maturationCycle === value)?._id ?? ''
		);
	};

	return {
		balanceUnit,
		setBalanceUnit,
		previewRows,
		isLoadingPreview:
			(balanceUnit !== 'percentage' && isFetchingPreview) || (isDynamic && isFetchingCycle),
		previewEmptyMessage,
		showParcellingFields,
		showParcellingPreview,
		resetParcellingFields,
		setParcellingType,
		isDynamic,
		maturationCycle,
		setMaturationCycle,
		cycleCalendar,
	};
};
